import { cn } from "@/lib/utils";
import { useScrollReveal } from "@/hooks/useScrollReveal";

const kpis = [
  { status: "green" as const, label: "New MRR", value: "₹9.7L", delta: "↑12%", delay: 200 },
  { status: "amber" as const, label: "Active Subs", value: "2,870", delta: "→", delay: 400 },
  { status: "red" as const, label: "NPS", value: "42", delta: "↓15%", delay: 600 },
];

const statusColors = {
  green: { dot: "bg-success", text: "text-success" },
  amber: { dot: "bg-warning", text: "text-warning" },
  red: { dot: "bg-danger", text: "text-danger" },
};

/**
 * WOW Moment 1: Interactive proof artifact.
 * A premium receipt/memo that builds itself line-by-line on scroll.
 */
export function MemoReceipt({ className }: { className?: string }) {
  const { ref, visible } = useScrollReveal(0.15);

  return (
    <div ref={ref} className={cn("max-w-md mx-auto", className)}>
      <div className="bg-card border border-border/60 rounded-3xl shadow-2xl overflow-hidden">
        {/* Receipt header */}
        <div
          className={cn(
            "px-6 pt-6 pb-4 border-b border-dashed border-border/40 transition-all duration-700",
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
          )}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[10px] font-semibold text-muted-foreground tracking-[0.25em] uppercase">
                Weekly Memo
              </p>
              <p className="text-xl font-bold mt-1 tabular-nums">Week 15</p>
            </div>
            <div className="text-right">
              <p className="text-xs text-muted-foreground font-medium">Waypoint Studio</p>
              <p className="text-[11px] text-muted-foreground">IST · Apr 2026</p>
            </div>
          </div>
        </div>

        {/* KPI rows — staggered reveal */}
        <div className="px-6 py-5 space-y-3">
          {kpis.map((kpi, i) => (
            <div
              key={i}
              className={cn(
                "flex items-center justify-between p-3.5 rounded-xl bg-muted/40 transition-all duration-500",
                visible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-6"
              )}
              style={{ transitionDelay: visible ? `${kpi.delay}ms` : "0ms" }}
            >
              <div className="flex items-center gap-3">
                <span
                  className={cn(
                    "h-2.5 w-2.5 rounded-full transition-transform duration-300",
                    statusColors[kpi.status].dot,
                    visible && "scale-100",
                    !visible && "scale-0"
                  )}
                  style={{ transitionDelay: visible ? `${kpi.delay + 200}ms` : "0ms" }}
                />
                <span className="text-sm font-medium">{kpi.label}</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm font-bold tabular-nums">{kpi.value}</span>
                <span
                  className={cn(
                    "text-xs font-semibold tabular-nums",
                    statusColors[kpi.status].text
                  )}
                >
                  {kpi.delta}
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* Focus area */}
        <div
          className={cn(
            "px-6 py-4 border-t border-dashed border-border/40 transition-all duration-700",
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
          )}
          style={{ transitionDelay: visible ? "800ms" : "0ms" }}
        >
          <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-[0.2em] mb-1.5">
            Focus
          </p>
          <p className="text-sm text-foreground leading-relaxed">
            NPS dropped — schedule user interviews this week.
          </p>
        </div>

        {/* Timestamp */}
        <div
          className={cn(
            "px-6 py-3 bg-muted/20 transition-all duration-700",
            visible ? "opacity-100" : "opacity-0"
          )}
          style={{ transitionDelay: visible ? "1000ms" : "0ms" }}
        >
          <p className="text-[10px] text-muted-foreground text-center tracking-[0.15em] uppercase">
            Generated Sun 11:00 AM IST · memo.app
          </p>
        </div>
      </div>
    </div>
  );
}
